import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The Sustainability Innovation e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/sustainability-innovation/`), sent as four screenshots
 * on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam şekilde,
 * aynısını, tıpkısını tasarla"*. Section for section that page's: the
 * two-column hero with the cover artwork beside it, the grey "What's Inside"
 * card with the cover pair and the five bullets, the "Why Download" card with
 * the stacked copies and three bullets, and the centred download ask at the
 * end. The copy is that page's copy, verbatim.
 *
 * Third e-book page after Transform Better and Agentic AI, and it follows
 * `ebook-agentic-ai.ts` in every place the reference and this site disagree —
 * the same three deliberate departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, centred over the copy column, rather
 *    than digitopia.co's small left-pinned "BEYOND COMPLIANCE" label. Every
 *    other hero here is that shape.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean
 *    the page's only call to action silently drops every lead. The `check`
 *    below says so, as the Agentic AI page's does.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book page does, asked for on 2026-08-28
 *    (*"en aşağıda önceden konuştuğumuz gibi testimonial template ini
 *    kullanalım"*). The card is CFN Kimya's: of the twenty in
 *    `content/testimonials.ts` it is the one about **sustainability** maturity
 *    rather than digital, which is this book's subject. Its words are the Hall
 *    of Fame's; that file is where a change to them belongs.
 *
 * **The slug is confirmed, and it is not the one the catalogue derived.**
 * `content/ebooks.ts` guessed `leading-the-charge-in-sustainability-innovation`
 * from the title; the screenshots came from
 * `digitopia.co/impact-labs/ebooks/sustainability-innovation/`, so the real
 * address is the short one. The catalogue entry was corrected to match, which
 * also closed that e-book's slug `check`.
 */
export const ebookSustainabilityInnovation: WrittenPage = {
  navLabel: 'Sustainability Innovation',
  title: 'Leading the Charge in Sustainability Innovation',
  description:
    'Beyond compliance: how sustainability innovation drives growth and resilience. Download the Sustainability Innovation e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Beyond Compliance',
      headline: 'Leading the Charge in Sustainability Innovation',
      body: [
        'In the modern business world, compliance isn’t enough. True success comes from '
          + 'innovation and proactive sustainability efforts. Our ebook shows how your business '
          + 'can go beyond mere compliance to achieve remarkable growth and resilience.',
      ],
      ctas: [{ label: 'Download', href: LINKS.sustainabilityInnovationEbookPdf }],
      photo: {
        src: '/photo/sustainability-innovation-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a glass sphere resting on moss, a green city reflected '
          + 'inside it.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take a
      // third of the sphere off the top and bottom.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE COVER ARTWORK IS 768×768. It is the largest copy of it anybody '
        + 'has sent; every other hero on the site is 2560px, and this one is painted '
        + 'at roughly its own width on a laptop with nothing left over for a retina '
        + 'screen. It is generated art rather than a photograph, so a bigger export '
        + 'should exist wherever it was made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/sustainability-innovation-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes. The same is true of the two mockups '
        + 'below it (900px wide, both), which are drawn smaller and are therefore '
        + 'less pressing.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'What’s Inside',
      points: [
        'The limitations of compliance-based sustainability and the benefits of proactive '
          + 'approaches.',
        'How to leverage sustainability for growth and innovation.',
        'The business case for sustainability, including financial, operational, and '
          + 'reputational benefits.',
        'Real-world examples from leading companies that have successfully integrated '
          + 'sustainability.',
        'Practical steps for measuring and enhancing your sustainability efforts.',
      ],
      ctas: [{ label: 'Download the Ebook', href: LINKS.sustainabilityInnovationEbookPdf }],
      photo: {
        src: '/photo/sustainability-innovation-ebook-covers.webp',
        alt:
          'Two copies of the Sustainability Innovation e-book, one behind the other, on a pale '
          + 'gradient.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Why Download',
      points: [
        'Learn how to turn sustainability from a compliance necessity into a strategic '
          + 'advantage.',
        'Discover innovative strategies to drive business growth and improve efficiency.',
        'Get inspired by real-world case studies of sustainability success.',
      ],
      ctas: [{ label: 'Download the Report', href: LINKS.sustainabilityInnovationEbookPdf }],
      photo: {
        src: '/photo/sustainability-innovation-ebook-stack.webp',
        alt: 'A stack of printed copies of the Sustainability Innovation e-book seen from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Don’t Miss Out! Download Your Free Ebook Now',
      sub:
        'Unleash the potential of sustainability innovation in your business. Get your copy of '
        + '“Beyond Compliance: Leading the Charge in Sustainability Innovation” and start your '
        + 'journey towards sustainable growth and competitive advantage.',
      cta: { label: 'Download the Ebook', href: LINKS.sustainabilityInnovationEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's CFN Kimya card, word for word — `content/testimonials.ts`
          // is where these words are edited, and the Turkish page carries its own
          // saying of them.
          quote:
            'Digital transformation and sustainability are two major business imperatives of '
            + 'modern business world. In that respect, assessing where we are and where we would '
            + 'like to be and having an aligned understanding and commitment among our management '
            + 'team is very precious. We chose Digitopia because of their professionalism and '
            + 'sound methodology in maturity models of both digital and sustainability '
            + 'transformation.',
          source: 'Fatma Köktaş, Board Member and CEO',
          company: 'CFN Kimya',
          logo: { name: 'CFN Kimya', src: '/logo/cfnkimya.webp' },
          portrait: { src: '/photo/portrait-fatma-koktas.png', alt: 'Fatma Köktaş' },
        },
      ],
    },
  ],
}
